import './euras-bank.scss';

import { Collapse } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { CustomSwitch } from '@/components/Shared/Switch/CustomSwitch';
import { Button, DocumentController } from '@/components/Shared/ui';
import { ReloadOutlined } from '@/media/antd-icons';
import { useAppSelector } from '@/store';
import type { FileType } from '@/types/uploadFiles';

import { EurasDocuments, EurasThirdStep } from '../../entities';
import useInstructionsText from '../../hooks/useInstructionsText';
import type { SendDocumentsHandler } from '../../widgets';
import { fileDict, type fileDictType } from './constants/files.dicts';
import { eubankOfferFinancedItems } from './constants/financing.data';
import useEurasForm from './hooks/useEurasForm';
import useEurasStatus from './hooks/useEurasStatus';
import useModerationDocuments from './hooks/useModerationDocuments';

interface IProps {
  isSubmitLoading: boolean;
  onSubmit: SendDocumentsHandler<{ with_pledge: boolean }>;
}

const EurasBank: React.FC<IProps> = ({ isSubmitLoading, onSubmit }) => {
  const { selectedBank, isVehicleInfoValid, isGrnFactoryCheckPassed } =
    useAppSelector((state) => state.financingStep);
  const { banksOffers } = useAppSelector((state) => state.offerStep);

  const [isPledgeRegister, setIsPledgeRegister] = useState(false);

  const { documentsOnModeration } = useModerationDocuments();
  const { files, methods, attachedCollapseFiles } = useEurasForm(
    documentsOnModeration
  );
  const { updateStatusLoading, handleChangeEuBankStatus } = useEurasStatus();

  useInstructionsText();

  const isHasFinancedOffer = useMemo(
    () => banksOffers.some((offer) => offer.status.code === 'financed'),
    [banksOffers]
  );

  const isOfferFinanced = selectedBank?.status.code === 'financed';
  const isPledgeMissing =
    selectedBank?.status.code ===
      'financed_waiting_for_modification_before_pledge' ||
    selectedBank?.status.code === 'financed_pledge_missing';

  const handleSubmit = (values: Record<string, FileType>) => {
    const copiedFiles = { ...values };
    // Подставляем нужный тип файлов
    if (copiedFiles['DGZL'] && copiedFiles['DGZL']?.base64)
      copiedFiles['SDGZ'] = copiedFiles['DGZL'];
    if (copiedFiles['LNBA'] && copiedFiles['LNBA']?.base64)
      copiedFiles['SLNB'] = copiedFiles['LNBA'];

    if (copiedFiles['DGZL']) delete copiedFiles['DGZL'];
    if (copiedFiles['LNBA']) delete copiedFiles['LNBA'];
    onSubmit(copiedFiles, {
      additionalData: { with_pledge: isPledgeRegister }
    });
  };

  useEffect(() => {
    setIsPledgeRegister(
      selectedBank?.status.code === 'financed_pledge_missing' ||
        selectedBank?.status.code ===
          'financed_waiting_for_modification_before_pledge'
    );
  }, [selectedBank?.status.code]);

  return (
    <>
      <div className="euras-bank__actions">
        <CustomSwitch
          value={isPledgeRegister}
          label={'pledgeRegister'}
          fieldsReadOnly={isOfferFinanced}
          type={'eurasBank'}
          labelTitle={'Регистрация залога'}
          onchangeCheckedHandler={setIsPledgeRegister}
        />
        {!isHasFinancedOffer && (
          <Button
            className="euras-bank__update-status"
            size="small"
            onClick={handleChangeEuBankStatus}
            isLoading={updateStatusLoading}
            isReversed
          >
            Обновить статус
            <ReloadOutlined />
          </Button>
        )}
      </div>

      {documentsOnModeration.length > 0 && (
        <div className="euras-bank__moderation-documents">
          <h2 className="euras-bank__moderation-title">
            Документы для переотправки:
          </h2>
          {documentsOnModeration.map((doc) => (
            <div
              className="euras-bank__moderation-document"
              key={doc.documentType}
            >
              {doc?.documentDescription || 'Все документы'}
            </div>
          ))}
        </div>
      )}

      <FormProvider {...methods}>
        <form
          className="financing-step__form"
          onSubmit={methods.handleSubmit(handleSubmit)}
        >
          {files.firstStepFiles?.map((file, index) => {
            if (index < 2) return null;
            return (
              <DocumentController
                key={file.type}
                fileLink={file.uuid}
                title={fileDict[file.type as fileDictType] || ''}
                downloadable
              />
            );
          })}
          {isVehicleInfoValid && (
            <>
              {files.secondStepFiles?.map((file, index) => {
                if (index < 2) return null;
                return (
                  <DocumentController
                    key={file.type}
                    fileLink={file.uuid}
                    title={fileDict[file.type as fileDictType] || ''}
                    downloadable
                  />
                );
              })}

              <EurasThirdStep files={files.thirdStepFiles} />

              {isPledgeMissing ? (
                eubankOfferFinancedItems.map((item) => (
                  <DocumentController
                    key={item.key}
                    control={methods.control}
                    name={item.key}
                    title={fileDict[item.key as fileDictType] || ''}
                    uploadable
                  />
                ))
              ) : !isOfferFinanced ? (
                <EurasDocuments
                  isPledgeMissing={isPledgeMissing}
                  isPledgeRegister={isPledgeRegister}
                  documentsOnModeration={documentsOnModeration}
                />
              ) : (
                files.financedStepFile.map((file, index) => {
                  if (index < 2) return null;
                  return (
                    <DocumentController
                      key={file.type}
                      title={fileDict[file.type as fileDictType] || ''}
                      fileLink={file.uuid}
                      downloadable
                    />
                  );
                })
              )}

              <Collapse
                className="financing-step__field"
                items={[
                  {
                    key: selectedBank?.id + (selectedBank?.extId || ''),
                    label: 'Прикрепленные файлы',
                    children: attachedCollapseFiles.length ? (
                      attachedCollapseFiles.map((data) => (
                        <DocumentController
                          key={data.uuid}
                          fileLink={data.uuid}
                          title={
                            fileDict[data.type as fileDictType] || data.type
                          }
                          downloadable
                          wrapperClassName="euras-bank__collapsed-document"
                        />
                      ))
                    ) : (
                      <p className="financing-step__no-documents">
                        Нет прикрепленных файлов
                      </p>
                    )
                  }
                ]}
              />

              {!isOfferFinanced && (
                <Button
                  className="financing-step__submit"
                  variant="filled-red"
                  disabled={
                    Object.values(methods.formState.errors).length !== 0 ||
                    !isGrnFactoryCheckPassed
                  }
                  isLoading={isSubmitLoading}
                >
                  Отправить документы
                </Button>
              )}
            </>
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default EurasBank;
